// DISCLAIMER
// Copyright 2026 ArangoDB GmbH, Cologne, Germany

import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Label, Loader, Menu, Message, Tab } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { ChartConfiguration, DefaultChartList } from "../../api/charts/v1/icharts";
import { ContentSegment, Confirm, ConfirmInfo, ErrorMessage, Section, SecondaryMenu } from "../../ui/lib";
import { hasSupportPermission, Permission } from "../../util/PermissionCache";
import { IWithRefreshProps } from "../../util/WithRefresh";
import ConfigurationTab from "./ConfigurationTab";
import DefaultChartsTab from "./DefaultChartsTab";
import { ConfirmWrite, errorText } from "./utils";

export interface ChartsViewArgs extends IWithRefreshProps, RouteComponentProps {}

const environmentColors: { [environment: string]: "grey" | "teal" | "blue" } = {
  development: "grey",
  staging: "teal",
  production: "blue",
};

const ChartsView = (props: ChartsViewArgs) => {
  const hasPermission = (permission: Permission) => hasSupportPermission(permission, props.hasPermissionByUrl);
  const canListDefaults = hasPermission("internal-dashboard.defaultchart.list");
  const canGetConfig = hasPermission("internal-dashboard.chartconfiguration.get");
  const canUpdateConfig = hasPermission("internal-dashboard.chartconfiguration.update");

  const [defaults, setDefaults] = useState<DefaultChartList>();
  const [config, setConfig] = useState<ChartConfiguration>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();
  const [confirmInfo, setConfirmInfo] = useState<ConfirmInfo | undefined>();

  const environment = (defaults && defaults.environment) || (config && config.environment) || "";

  const reload = async () => {
    setLoading(true);
    try {
      const [defaultsResult, configResult] = await Promise.all([
        canListDefaults ? apiClients.idashboardClient.ListDefaultCharts() : Promise.resolve(undefined),
        canGetConfig ? apiClients.idashboardClient.GetChartConfiguration() : Promise.resolve(undefined),
      ]);
      setDefaults(defaultsResult);
      setConfig(configResult);
      setError(undefined);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // Changes are confirmed in production, and always for destructive actions.
  const confirm: ConfirmWrite = (options, action) => {
    const run = async () => {
      setConfirmInfo(undefined);
      await action();
    };
    if (!options.always && environment !== "production") {
      run();
      return;
    }
    setConfirmInfo({
      header: options.header,
      content: options.content,
      warning: options.warning || (environment === "production" ? "This is the production environment." : undefined),
      confirm: options.confirm,
      onConfirm: run,
      onDenied: () => setConfirmInfo(undefined),
    });
  };

  const panes = [
    {
      menuItem: "Default charts",
      render: () => (
        <Tab.Pane>
          <DefaultChartsTab
            defaults={defaults}
            config={config}
            canEdit={canGetConfig && canUpdateConfig}
            confirm={confirm}
            onReload={reload}
            onError={setError}
          />
        </Tab.Pane>
      ),
    },
  ];
  if (canGetConfig) {
    panes.push({
      menuItem: "Configuration",
      render: () => (
        <Tab.Pane>
          <ConfigurationTab config={config} canEdit={canUpdateConfig} confirm={confirm} onReload={reload} onError={setError} />
        </Tab.Pane>
      ),
    });
  }

  return (
    <ContentSegment>
      <Confirm confirmInfo={confirmInfo} />
      <SecondaryMenu>
        <Menu.Item header>Charts {loading ? <Loader size="mini" inline active /> : ""}</Menu.Item>
        {environment && (
          <Menu.Item>
            <Label color={environmentColors[environment]}>{environment}</Label>
          </Menu.Item>
        )}
      </SecondaryMenu>
      <Section>
        <ErrorMessage active={!!error} onDismiss={() => setError(undefined)} message={errorText(error)} />
      </Section>
      {!canListDefaults ? (
        <Message warning content="You do not have permission to list the default charts." />
      ) : (
        <>
          {environment === "production" && (
            <Message
              warning
              content="This is the production environment. Changes apply to new deployments, and to existing deployments when they are synced."
            />
          )}
          <Tab menu={{ secondary: true, pointing: true }} panes={panes} />
        </>
      )}
    </ContentSegment>
  );
};

export default ChartsView;
